import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin } from "lucide-react";
import { DARK_MAP_STYLES, useIsDarkClass } from "@/lib/map-styles";

export interface RealtorListingMapPoint {
  id: number;
  address: string;
  city: string | null;
  state: string | null;
  price: string | number;
  latitude: string | number | null;
  longitude: string | number | null;
}

interface RealtorListingsMapProps {
  listings: RealtorListingMapPoint[];
}

function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null) return "—";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

function toNumOrNull(val: string | number | null | undefined): number | null {
  if (val == null) return null;
  const n = typeof val === "string" ? parseFloat(val) : val;
  return Number.isFinite(n) ? n : null;
}

export default function RealtorListingsMap({ listings }: RealtorListingsMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const { data: mapsKeyData, isLoading: keyLoading } = useQuery<{ key: string }>({
    queryKey: ["/api/app/maps-key"],
  });
  const apiKey = mapsKeyData?.key || "";
  const isDark = useIsDarkClass();

  const loadGoogleMaps = useCallback(() => {
    if (!apiKey) return;
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 50);
    };
    script.onerror = () => setMapError("Failed to load Google Maps");
    document.head.appendChild(script);
  }, [apiKey]);

  useEffect(() => {
    loadGoogleMaps();
  }, [loadGoogleMaps]);

  const mappable = listings
    .map((l) => ({ ...l, _lat: toNumOrNull(l.latitude), _lng: toNumOrNull(l.longitude) }))
    .filter((l) => l._lat != null && l._lng != null);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 10,
        center: { lat: 33.749, lng: -84.388 },
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: isDark ? DARK_MAP_STYLES : [],
      });
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    const map = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current!;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (mappable.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    mappable.forEach((l) => {
      const position = { lat: l._lat as number, lng: l._lng as number };
      bounds.extend(position);

      const marker = new google.maps.Marker({
        map,
        position,
        title: l.address,
        icon: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
      });

      marker.addListener("click", () => {
        const container = document.createElement("div");
        container.style.cssText = "padding:8px;max-width:260px;font-family:system-ui,sans-serif;";

        const addrEl = document.createElement("div");
        addrEl.style.cssText = "font-weight:600;font-size:14px;margin-bottom:2px;";
        addrEl.textContent = l.address;
        container.appendChild(addrEl);

        if (l.city || l.state) {
          const sub = document.createElement("div");
          sub.style.cssText = "font-size:12px;color:#6b7280;margin-bottom:6px;";
          sub.textContent = [l.city, l.state].filter(Boolean).join(", ");
          container.appendChild(sub);
        }

        const priceEl = document.createElement("div");
        priceEl.style.cssText = "font-size:14px;font-weight:700;color:#111827;margin-bottom:8px;";
        priceEl.textContent = formatCurrency(l.price);
        container.appendChild(priceEl);

        const link = document.createElement("a");
        link.href = `/app/realtor-listings/${l.id}`;
        link.style.cssText = "font-size:12px;color:#7c3aed;text-decoration:none;font-weight:500;";
        link.textContent = "View Details →";
        link.setAttribute("data-testid", `link-map-realtor-listing-${l.id}`);
        container.appendChild(link);

        infoWindow.setContent(container);
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    if (mappable.length === 1) {
      map.setCenter({ lat: mappable[0]._lat as number, lng: mappable[0]._lng as number });
      map.setZoom(13);
    } else {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, [mapLoaded, listings, isDark]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({ styles: isDark ? DARK_MAP_STYLES : [] });
  }, [isDark]);

  if (keyLoading) {
    return <Skeleton className="h-[500px] w-full rounded-lg" data-testid="realtor-map-loading" />;
  }

  if (!apiKey) {
    return (
      <div className="border rounded-lg p-6 text-center text-gray-500" data-testid="realtor-map-no-key">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Map view is unavailable.</p>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="border rounded-lg p-6 text-center text-gray-500" data-testid="realtor-map-error">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">{mapError}</p>
      </div>
    );
  }

  if (!mapLoaded) {
    return <Skeleton className="h-[500px] w-full rounded-lg" data-testid="realtor-map-loading" />;
  }

  return (
    <div data-testid="realtor-listings-map-container">
      <div
        ref={mapRef}
        className="w-full h-[500px] rounded-lg border shadow-sm"
        data-testid="realtor-listings-map"
      />
      {mappable.length === 0 && listings.length > 0 && (
        <p className="text-sm text-gray-500 mt-2 text-center" data-testid="text-no-mappable-listings">
          No listings have location data to plot on the map.
        </p>
      )}
    </div>
  );
}
